import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {Tab, Tabs} from "@mui/material";
import {BuyerBookingTabs, transformBooking} from '../../components/bookingReports/buyer/data';
import {formatTabHeader} from '../../components/bookingReports/data';
import AdjustBookingDialog from '../../components/bookingReports/AdjustBookingDialog';
import {TabContext} from "@mui/lab";
import BookingTab from '../../components/bookingReports/BookingTab';
import BuyerBookingFilter from '../../components/bookingReports/buyer/BuyerBookingFilter';
import {withSnackbar} from '../../components/hocs/withSnackbar';
import {getErrorMessageForNonStandardAndStandardResponse} from '../../util/NetworkErrorUtil';
import {getBuyerBookings} from '../../components/bookedSpaces/request/booked-spaces-requests';
import {hasAdminAccess} from '../../util/AccountUtils';
import {BookingTabPanel} from '../view-styles/bookingStyles';
import useUrlQueryParams from "../../hooks/useUrlQueryParams";
import {parseCurrencyValue} from "../../util/PaymentUtils";
import {
  AccountType,
  BookingStatus,
  BookingStatusesEligibleForCancellation,
  BookingStatusesEligibleForPaymentMethodUpdate,
  DateFormats
} from '../../components/constants/securspace-constants';
import BuyerActiveDialogs from '../../components/bookingReports/buyer/dialogs/BuyerActiveDialogs';
import BuyerPendingDialogs from '../../components/bookingReports/buyer/dialogs/BuyerPendingDialogs';
import Busy from '../../components/Busy';
import {cancelBooking, cancelBookingSubscription} from '../requests/booking-requests';
import {startDateLessThan24Hours} from '../../util/BookingUtil';
import {useNavigate} from 'react-router';
import moment from 'moment';
import {AppContext} from "../../context/app-context";
import {ObservableVariables} from '../../components/constants/observable-constants';
import {useBookingReasonDeclinedOptions} from '../../hooks/useBookingReasonDeclinedOptions';
import {SecurSpaceContainer, SecurSpaceReportContainer, SecurSpaceReportTitle} from "../../components/common";
import {SortFilterContainer} from "../../components/common/reports/sortFilter";
import {TabContextContainer} from "../../components/common/reports/tabs/TabContextContainer";
import {TabsContainer} from "../../components/common/reports/tabs/TabsContainer";

export const modalTypes = {
  complete: "COMPLETE",
  cancel_request: 'CANCEL',
  payment: "PAYMENT",
  review: "REVIEW",
  termsOnly: "TERMS_ONLY",
  activeBookingCancel: 'CANCEL_ACTIVE',
  endMonthly: 'END_MONTHLY',
  updatePayment: 'UPDATE_PAYMENT'
};

const BuyerBooking = (props) => {
  const {account, snackbarShowMessage} = props;
  const appContext = useContext(AppContext);

  const urlParams = useUrlQueryParams();
  const navigate = useNavigate();

  const getDefaultTab = useCallback((status) => {
    return BuyerBookingTabs[status?.toUpperCase()] || BuyerBookingTabs.ACTIVE;
  }, []);

  const defaultAdjustBooking = {open: false, booking: null};

  const [initialTab, setInitialTab] = useState(getDefaultTab(urlParams.status || ""));
  const [initialBookingNumber, setInitialBookingNumber] = useState(urlParams.bookingNumber || "");
  const [currentTab, setCurrentTab] = useState(initialTab);
  const [activeBookings, setActiveBookings] = useState()
  const [inactiveBookings, setInactiveBookings] = useState();
  const [failedBookings, setFailedBookings] = useState();
  const [pendingBookings, setPendingBookings] = useState();
  const [activePage, setActivePage] = useState(0);
  const [failedPage, setFailedPage] = useState(0);
  const [inactivePage, setInactivePage] = useState(0);
  const [pendingPage, setPendingPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [activeBookingCount, setActiveBookingCount] = useState(0);
  const [failedBookingCount, setFailedBookingCount] = useState(0);
  const [inactiveBookingCount, setInactiveBookingCount] = useState(0);
  const [pendingBookingCount, setPendingBookingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(null);
  const [bookingToEdit, setBookingToEdit] = useState(defaultAdjustBooking);
  const [showModal, setShowModal] = useState({show: false, type: modalTypes.payment})

  const {reasonDeclinedMap, loading: loadingReasonDeclined} = useBookingReasonDeclinedOptions();

  const userIsAdmin = hasAdminAccess(account);

  const scrollPositions = useRef({});

  const handleTabChange = (_event, value) => {
    // Save current scroll position
    scrollPositions.current[currentTab] = window.scrollY;

    setCurrentTab(value || BuyerBookingTabs.ACTIVE);

    // Restore scroll position for new tab
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPositions.current[value] || 0);
    });
  };

  const onCloseDialog = () => {
    setBookingToEdit(defaultAdjustBooking)
  }

  const handleCloseModals = () => {
    setShowModal(prev => ({...prev, show: false}));
    onCloseDialog();
  };

  const onDialogSuccess = () => {
    ObservableVariables.PENDING_APPROVAL_COUNT.refreshData(appContext);
    handleCloseModals();
    loadBookings(BuyerBookingTabs.ACTIVE, setActiveBookings, setActiveBookingCount, activePage, rowsPerPage);
    loadBookings(BuyerBookingTabs.INACTIVE, setInactiveBookings, setInactiveBookingCount, inactivePage, rowsPerPage);
    loadBookings(BuyerBookingTabs.FAILED, setFailedBookings, setFailedBookingCount, failedPage, rowsPerPage);
    loadBookings(BuyerBookingTabs.PENDING, setPendingBookings, setPendingBookingCount, pendingPage, rowsPerPage);
  }

  const handleAdjustSpaces = (spaces) => {
    const {booking} = bookingToEdit;

    let newBooking = {...booking, numberOfSpaces: spaces}

    setBookingToEdit(prev => ({...prev, booking: newBooking}))
  }

  const handleAdjustEndDate = (date) => {
    const {booking} = bookingToEdit;

    let newBooking = {...booking, endDate: date}

    setBookingToEdit(prev => ({...prev, booking: newBooking}));
  }

  /**
   * Handle adjusting booking fields
   * @param event where the target name is the booking field to adjust
   */
  const handleAdjustBooking = (event) => {
    const {booking} = bookingToEdit;
    const {name, value} = event.target;
    let newBooking = {...booking};

    if (name === 'brokeredBuyerChargedPerOccurrence' || name === 'brokeredSupplierPaidPerOccurrence') {
      newBooking[name] = parseCurrencyValue(value);
    } else {
      newBooking[name] = value;
    }
    setBookingToEdit(prev => ({...prev, booking: newBooking}));
  }

  const handleEndBooking = (booking) => {
    if (booking.bookingSubscriptionStatus === "ACTIVE" && BookingStatusesEligibleForCancellation.includes(booking.status)) {
      // A pre-start ACH monthly booking is a full cancellation (with refund), not just ending future
      // renewals. Route it to the cancel+refund flow; every other status keeps the End Booking flow.
      if (booking.status === BookingStatus.PROCESSING_ACH_PAYMENT && !startDateLessThan24Hours(booking)) {
        setBookingToEdit(booking);
        setShowModal({show: true, type: modalTypes.activeBookingCancel});
      } else {
        setBookingToEdit(booking);
        setShowModal({show: true, type: modalTypes.endMonthly});
      }
    } else if (booking.status === BookingStatus.PENDING || ((booking.status === BookingStatus.APPROVED || booking.status === BookingStatus.PROCESSING_ACH_PAYMENT) && booking.frequency !== "RECURRING")) {
      if (!startDateLessThan24Hours(booking)) {
        setBookingToEdit(booking);
        setShowModal({show: true, type: modalTypes.activeBookingCancel});
      } else {
        snackbarShowMessage("This booking is not eligible for cancellation because it is less than 24 hours before the booking start date.", "warning");
      }
    } else {
      // Remove this when we can programmatically disable the end bookings button
      snackbarShowMessage("This booking is not eligible for cancellation.", "warning");
    }
  };

  const handleUpdateBooking = (booking) => {
    if (BookingStatusesEligibleForPaymentMethodUpdate.includes(booking.status)) {
      setBookingToEdit(booking);
      setShowModal({show: true, type: modalTypes.updatePayment});
    } else {
      // Remove this when we can programmatically disable the update payment method button
      snackbarShowMessage("This booking is not eligible for payment method update.", "warning");
    }
  };

  const handleCancelBooking = () => {
    Busy.set(true);
    cancelBooking({bookingId: bookingToEdit.id}).then((response) => {
      const cancelledBooking = response.body;
      snackbarShowMessage(`Successfully cancelled booking ${cancelledBooking.orderNumber}`, "success");
      onDialogSuccess();
    }).catch((error) => {
      snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), "error");
    }).finally(() => {
      Busy.set(false);
    });
  };

  const handleMonthlyBookingCancellation = () => {
    Busy.set(true);
    cancelBookingSubscription({bookingId: bookingToEdit.id}).then((response) => {
      const canceledBooking = response.body;
      snackbarShowMessage(`Subscription for booking ${canceledBooking.orderNumber} will end on ${moment(canceledBooking.endDate).format(DateFormats.DAY)}`, "success");
      onDialogSuccess();
    }).catch((error) => {
      snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), "error");
    }).finally(() => {
      Busy.set(false);
    });
  };

  const handleMakePayment = (booking) => {
    navigate(`/bookings/${booking.id}/failedPayment`);
  };

  const shouldShowTermsOnlyModal = (booking) => {
    return booking.manualPayment ||
      (booking.brokeredBuyerChargedPerOccurrence === 0 &&
        booking.brokeredSupplierPaidPerOccurrence === 0 &&
        booking.chargeOverages === false);
  }

  const handleShowPaymentModal = (booking) => {
    setBookingToEdit(booking);
    if (shouldShowTermsOnlyModal(booking)) {
      setShowModal({show: true, type: modalTypes.termsOnly})
    } else {
      setShowModal({show: true, type: modalTypes.payment});
    }
  }

  const handleShowCancelBookingRequestModal = (booking) => {
    setBookingToEdit(booking)
    setShowModal({show: true, type: modalTypes.cancel_request})
  }

  const loadBookings = useCallback((
    status,
    setBookings,
    setBookingCount,
    page,
    pageSize
  ) => {
    const {id} = account;

    if (id && query) {
      setLoading(true);
      const requestParams = {
        accountId: account.id,
        locationId: query.selectedLocation?.id || '',
        activeStatus: status,
        page: page,
        size: pageSize,
        ...query,
      };

      getBuyerBookings(requestParams)
        .then(({body}) => {
          const {count} = body;
          setBookingCount(count);
          const data = body.content?.map((booking) => {
            const transformedBooking = transformBooking(booking);
            // Map reasonDeclined enum to description if available
            if (transformedBooking.reasonDeclined && reasonDeclinedMap?.[transformedBooking.reasonDeclined]) {
              transformedBooking.reasonDeclined = reasonDeclinedMap[transformedBooking.reasonDeclined];
            }
            return transformedBooking;
          });
          setBookings(data);
        })
        .catch(err => {
          snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), "error", 15000)
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [account, query, snackbarShowMessage, reasonDeclinedMap]);

  useEffect(() => {
    // Prevent automatic scroll restoration on re-renders
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  useEffect(() => {
    const loadAllBookings = async () => {
      // Wait for reason declined options to load before fetching bookings
      if (!query || !account?.id || loadingReasonDeclined) return;

      setLoading(true);
      try {
        await Promise.all([
          loadBookings(BuyerBookingTabs.ACTIVE, setActiveBookings, setActiveBookingCount, activePage, rowsPerPage),
          loadBookings(BuyerBookingTabs.INACTIVE, setInactiveBookings, setInactiveBookingCount, inactivePage, rowsPerPage),
          loadBookings(BuyerBookingTabs.FAILED, setFailedBookings, setFailedBookingCount, failedPage, rowsPerPage),
          loadBookings(BuyerBookingTabs.PENDING, setPendingBookings, setPendingBookingCount, pendingPage, rowsPerPage)
        ]);
      } catch (error) {
        // Error handling is already in loadBookings
      } finally {
        setLoading(false);
      }
    };

    loadAllBookings();
  }, [loadBookings, rowsPerPage, activePage, inactivePage, failedPage, pendingPage, query, account?.id, loadingReasonDeclined]);

  useEffect(() => {
    if (failedBookings && inactiveBookings && pendingBookings && activeBookings) {
      if (initialTab) {
        setInitialTab(null);
      } else if (initialBookingNumber) {
        setInitialBookingNumber(null);
        if (failedBookings.length) {
          setCurrentTab(BuyerBookingTabs.FAILED)
        } else if (inactiveBookings.length) {
          setCurrentTab(BuyerBookingTabs.INACTIVE)
        } else if (pendingBookings.length) {
          setCurrentTab(BuyerBookingTabs.PENDING)
        } else { //default to active
          setCurrentTab(BuyerBookingTabs.ACTIVE)
        }
      }
    }
  }, [initialTab, initialBookingNumber]);

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <SecurSpaceReportTitle>Bookings</SecurSpaceReportTitle>
        <SortFilterContainer mt={3}>
          <BuyerBookingFilter setQuery={setQuery}/>
        </SortFilterContainer>
        <TabContextContainer>
          <TabContext value={currentTab}>
            <TabsContainer>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                aria-label={'Bookings Tabs'}
                textColor={'secondary'}
                variant={'fullWidth'}
              >
                <Tab value={BuyerBookingTabs.ACTIVE}
                     label={formatTabHeader(BuyerBookingTabs.ACTIVE, activeBookingCount)}/>
                <Tab value={BuyerBookingTabs.PENDING}
                     label={formatTabHeader(BuyerBookingTabs.PENDING, pendingBookingCount)}/>
                <Tab value={BuyerBookingTabs.INACTIVE}
                     label={formatTabHeader(BuyerBookingTabs.INACTIVE, inactiveBookingCount)}/>
                <Tab value={BuyerBookingTabs.FAILED}
                     label={formatTabHeader(BuyerBookingTabs.FAILED, failedBookingCount)}/>
              </Tabs>
            </TabsContainer>

            <BookingTabPanel value={BuyerBookingTabs.ACTIVE} keepMounted>
              <BookingTab
                bookings={activeBookings}
                loading={loading}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={activePage}
                setPage={setActivePage}
                count={activeBookingCount}
                status={BuyerBookingTabs.ACTIVE}
                userIsAdmin={userIsAdmin}
                handleSelectAdjustBooking={handleUpdateBooking}
                handleEndBooking={handleEndBooking}
                handleShowCancelBookingRequestModal={handleShowCancelBookingRequestModal}
                accountType={AccountType.BUYER}
                handleMakePayment={handleMakePayment}
              />
            </BookingTabPanel>
            <BookingTabPanel value={BuyerBookingTabs.PENDING}>
              <BookingTab
                bookings={pendingBookings}
                loading={loading}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={pendingPage}
                setPage={setPendingPage}
                count={pendingBookingCount}
                status={BuyerBookingTabs.PENDING}
                userIsAdmin={userIsAdmin}
                handleSelectAdjustBooking={handleUpdateBooking}
                handleShowPaymentModal={handleShowPaymentModal}
                handleShowCancelBookingRequestModal={handleShowCancelBookingRequestModal}
                accountType={AccountType.BUYER}
              />
            </BookingTabPanel>
            <BookingTabPanel value={BuyerBookingTabs.INACTIVE}>
              <BookingTab
                bookings={inactiveBookings}
                loading={loading}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={inactivePage}
                setPage={setInactivePage}
                count={inactiveBookingCount}
                status={BuyerBookingTabs.INACTIVE}
                userIsAdmin={userIsAdmin}
                handleSelectAdjustBooking={handleUpdateBooking}
                handleMakePayment={handleMakePayment}
                accountType={AccountType.BUYER}
              />
            </BookingTabPanel>
            <BookingTabPanel value={BuyerBookingTabs.FAILED}>
              <BookingTab
                bookings={failedBookings}
                loading={loading}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={failedPage}
                setPage={setFailedPage}
                count={failedBookingCount}
                status={BuyerBookingTabs.FAILED}
                userIsAdmin={userIsAdmin}
                handleSelectAdjustBooking={handleUpdateBooking}
                handleEndBooking={handleEndBooking}
                handleMakePayment={handleMakePayment}
                accountType={AccountType.BUYER}
              />
            </BookingTabPanel>
          </TabContext>
        </TabContextContainer>
        <AdjustBookingDialog
          open={bookingToEdit.open}
          onConfirm={onDialogSuccess}
          onClose={onCloseDialog}
          type={bookingToEdit.type}
          booking={bookingToEdit.booking}
          handleAdjustEndDate={handleAdjustEndDate}
          handleAdjustSpaces={handleAdjustSpaces}
          handleAdjustBooking={handleAdjustBooking}
        />
        <BuyerActiveDialogs
          account={account}
          bookingToEdit={bookingToEdit}
          showModal={showModal}
          handleCloseModals={handleCloseModals}
          handleBookingCancellation={handleCancelBooking}
          handleMonthlyBookingCancellation={handleMonthlyBookingCancellation}
          handleUpdatePaymentMethod={onDialogSuccess}
        />
        <BuyerPendingDialogs
          account={account}
          bookingBeingActioned={bookingToEdit}
          showModal={showModal}
          setShowModal={setShowModal}
          handleCloseModals={handleCloseModals}
          onDialogSuccess={onDialogSuccess}
          handleCancelBooking={handleCancelBooking}
        />
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  );
};

export default withSnackbar(BuyerBooking);
